import { nanoid } from 'nanoid'
import type {
  Coassign_User_Insert_Input,
  Coassign_User_To_Job_Title_Insert_Input,
  Coassign_User_To_Job_Skill_Insert_Input,
  Coassign_Employment_Insert_Input,
  Coassign_User_To_Department_Insert_Input,
  MutateCreateFirebaseUserMutationVariables
} from '@/gql/graphql'
import { InsertEmploymentDocument } from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import type { User } from '../user'
import {
  insertUserMutationDocument,
  insertUserToDepartmentMutationDocument,
  insertUserToJobTitleMutationDocument,
  insertUserToJobSkillMutationDocument,
  mutateCreateFirebaseUser
} from './userMutations'

export function addUserMutationQuery(
  user: User,
  firebaseTenantId: string,
  commitId: string
): MutationOperationAndVariables[] {
  const id = user.id
  const email = user.email
  if (!id || !firebaseTenantId || !email)
    throw new Error('add user required parameter id not found')
  const mutations: MutationOperationAndVariables[] = []
  mutations.push(buildAddMutationForUserDocument(id, user, commitId))
  if (user.departmentIds) {
    mutations.push(buildAddMutationForDepartmentDocuments(id, user.departmentIds, commitId))
  }
  if (user.jobTitleIds) {
    mutations.push(buildAddMutationForJobTitleDocuments(id, user.jobTitleIds, commitId))
  }
  if (user.jobSkillIds) {
    mutations.push(buildAddMutationForJobSkillDocuments(id, user.jobSkillIds, commitId))
  }
  mutations.push(buildAddMutationForInitEmploymentDocument(id, commitId))
  mutations.push(buildAddMutationForFirebaseDocument(firebaseTenantId, email))
  return mutations
}

function buildAddMutationForUserDocument(
  id: string,
  user: User,
  commitId: string
): MutationOperationAndVariables {
  const variables: Coassign_User_Insert_Input = {
    id: id,
    commit_id: commitId,
    name: user.name,
    role: user.role,
    email: user.email,
    note: user.note,
    available: user.available,
    display_order: user.display_order
  }
  return { mutation: insertUserMutationDocument, variables: { input: variables } }
}

function buildAddMutationForDepartmentDocuments(
  user_id: string,
  departments: string[],
  commitId: string
): MutationOperationAndVariables {
  const variables: Coassign_User_To_Department_Insert_Input[] = []
  departments.forEach((departmentId) => {
    variables.push({ commit_id: commitId, user_id: user_id, department_id: departmentId })
  })
  return { mutation: insertUserToDepartmentMutationDocument, variables: { input: variables } }
}

function buildAddMutationForJobTitleDocuments(
  user_id: string,
  job_titles: string[],
  commitId: string
): MutationOperationAndVariables {
  const variables: Coassign_User_To_Job_Title_Insert_Input[] = []
  job_titles.forEach((jobTitleId) => {
    variables.push({ commit_id: commitId, user_id: user_id, job_title_id: jobTitleId })
  })
  return { mutation: insertUserToJobTitleMutationDocument, variables: { input: variables } }
}

function buildAddMutationForJobSkillDocuments(
  user_id: string,
  job_skills: string[],
  commitId: string
): MutationOperationAndVariables {
  const variables: Coassign_User_To_Job_Skill_Insert_Input[] = []
  job_skills.forEach((jobSkillId) => {
    variables.push({ commit_id: commitId, user_id: user_id, job_skill_id: jobSkillId })
  })
  return { mutation: insertUserToJobSkillMutationDocument, variables: { input: variables } }
}

// Employmentを別のstageに分けると消された時に不整合になるのでuser.mutation内で実施
function buildAddMutationForInitEmploymentDocument(
  user_id: string,
  commitId: string
): MutationOperationAndVariables {
  const employmentId = nanoid()
  const variables: Coassign_Employment_Insert_Input = {
    id: employmentId,
    commit_id: commitId,
    user_id: user_id,
    type: 'MONTHLY',
    cost: 0
  }
  return { mutation: InsertEmploymentDocument, variables: { input: variables } }
}

function buildAddMutationForFirebaseDocument(
  firebaseTenantId: string,
  email: string
): MutationOperationAndVariables {
  const subDomain = window.location.hostname.split('.').shift()
  if (subDomain === undefined) throw new Error('cannot get subdomain')
  const variables: MutateCreateFirebaseUserMutationVariables = {
    email: email,
    firebase_tenant_id: firebaseTenantId,
    subdomain: subDomain
  }
  return { mutation: mutateCreateFirebaseUser, variables: variables }
}
